import { PositionsTable } from '@Components/Tables';
import {
    type ApiInvoice,
    type ApiTransaction,
    type EnumInvoiceType,
    EnumFrontendLayoutSetting,
    type EnumPaymentMethod,
    type EnumProcessState,
    type EnumTaxationMode,
    getEnumPaymentMethodLabel,
} from '@Model';
import { FrameInvoiceTransaction } from '@Modules/Billing';
import { type AsyncStateUpdate, type Column, useEditSubObject } from '@synqo/frontend-component-library';
import { memo } from 'react';

// @ai-generated 2026-09-18T07:55:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5

const transactionColumns: Column<ApiTransaction>[] = [
    { name: 'date', label: 'Datum', type: 'date', filterVariant: 'day-range' },
    { name: 'amount', label: 'Betrag', type: 'currency' },
    {
        name: 'payment_method',
        label: 'Zahlungsart',
        renderCell: ({ cellData }) => getEnumPaymentMethodLabel(cellData as EnumPaymentMethod),
    },
];

interface IProps {
    baseApiObjectPath: string;
    requestUpdateState: AsyncStateUpdate<ApiInvoice>;
    stateQueueDepth: number;
    refetchData: () => Promise<boolean>;

    transactions: ApiTransaction[];
    taxationMode: EnumTaxationMode;
    processState: EnumProcessState;
    invoiceType: EnumInvoiceType;

    canEditTransactions: boolean;

    /** A cash invoice is collected through the header action, so it takes no payment by hand. */
    isCashInvoice: boolean;
    collectionActionText: string;
}

export const InvoiceTransactionsSection = memo(function InvoiceTransactionsSection({
    baseApiObjectPath,
    requestUpdateState,
    stateQueueDepth,
    refetchData,
    transactions,
    taxationMode,
    processState,
    invoiceType,
    canEditTransactions,
    isCashInvoice,
    collectionActionText,
}: IProps) {
    const {
        addSubObject: addTransaction,
        requestUpdateSubObject: requestUpdateTransaction,
        deleteSubObjects: deleteTransactions,
    } = useEditSubObject<ApiTransaction, ApiInvoice>(baseApiObjectPath, 'transactions', requestUpdateState);

    return (
        <PositionsTable<ApiTransaction>
            layoutSettingsContext={EnumFrontendLayoutSetting.InvoiceTransactionsTableLayout}
            items={transactions}
            columns={transactionColumns}
            name="InvoiceTransactions"
            editable={canEditTransactions}
            addActionProps={{
                text: 'Zahlung hinzufügen',
                tooltip: isCashInvoice ? `Bitte nutzen Sie die Option '${collectionActionText}'` : undefined,
            }}
            deleteActionProps={{
                contextLabel: ['Zahlung', 'Zahlungen'],
            }}
            canCreate={canEditTransactions && !isCashInvoice}
            onAdd={addTransaction}
            onDelete={deleteTransactions}
            renderSubComponent={(row) => (
                <FrameInvoiceTransaction
                    key={row.id}
                    data={row}
                    meta={undefined}
                    canEdit={canEditTransactions}
                    apiResourcePath={`${baseApiObjectPath}/transactions`}
                    requestUpdateState={(u) => requestUpdateTransaction(u, row.id)}
                    stateQueueDepth={stateQueueDepth}
                    refetchData={refetchData}
                    priceDisplay={taxationMode}
                    processState={processState}
                    invoiceType={invoiceType}
                />
            )}
        />
    );
});
